import React from 'react'
import { Button, Checkbox, Input, Label, Modal, Textarea } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import type { FinanceCostCategory, FinanceFixedCost } from '../../services/financeCostsService'
import { upsertFinanceFixedCost } from '../../services/financeCostsService'
import { centsToBrlInput, parseBrlToCents } from '../../utils/money'
import { monthInputToMonthStartDate, monthStartDateToMonthInput } from '../../utils/month'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  categories: FinanceCostCategory[]
  initial?: FinanceFixedCost | null
}

export function FinanceFixedCostModal({ open, onClose, onSaved, categories, initial }: Props) {
  const { add: toast } = useToast()
  const [categoryId, setCategoryId] = React.useState<string>(initial?.category_id || '')
  const [name, setName] = React.useState(initial?.name || '')
  const [amountBrl, setAmountBrl] = React.useState<string>(() => centsToBrlInput(initial?.amount_cents || 0))
  const [startMonth, setStartMonth] = React.useState<string>(() => (initial?.start_month ? monthStartDateToMonthInput(initial.start_month) : ''))
  const [noEnd, setNoEnd] = React.useState<boolean>(() => !initial?.end_month)
  const [endMonth, setEndMonth] = React.useState<string>(() => (initial?.end_month ? monthStartDateToMonthInput(initial.end_month) : ''))
  const [notes, setNotes] = React.useState<string>(initial?.notes || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setCategoryId(initial?.category_id || '')
    setName(initial?.name || '')
    setAmountBrl(centsToBrlInput(initial?.amount_cents || 0))
    setStartMonth(initial?.start_month ? monthStartDateToMonthInput(initial.start_month) : '')
    setNoEnd(!initial?.end_month)
    setEndMonth(initial?.end_month ? monthStartDateToMonthInput(initial.end_month) : '')
    setNotes(initial?.notes || '')
    setSaving(false)
    setError(null)
  }, [open, initial])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      const start_month = monthInputToMonthStartDate(startMonth)
      const end_month = noEnd ? null : (endMonth ? monthInputToMonthStartDate(endMonth) : null)
      const amount_cents = parseBrlToCents(amountBrl)
      await upsertFinanceFixedCost({
        id: initial?.id,
        category_id: categoryId ? categoryId : null,
        name,
        amount_cents,
        currency: 'BRL',
        start_month,
        end_month,
        notes: notes.trim() ? notes.trim() : null,
      })
      toast({ title: 'Custo recorrente salvo', description: name.trim(), variant: 'success', soft: true })
      onSaved()
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar custo recorrente')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <div className="mb-4">
        <h2 className="text-lg font-medium">{initial?.id ? 'Editar custo recorrente' : 'Novo custo recorrente'}</h2>
      </div>
      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        {error && <div className="sm:col-span-2 text-sm text-red-400">{error}</div>}

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Nome</Label>
          <Input value={name} onChange={(e) => setName((e.target as HTMLInputElement).value)} placeholder="Ex.: Agência de marketing" />
        </div>

        <div className="space-y-1.5">
          <Label>Categoria</Label>
          <select
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">(Sem categoria)</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label>Valor (R$)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={amountBrl}
            onChange={(e) => setAmountBrl((e.target as HTMLInputElement).value)}
            placeholder="0,00"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Início (mês)</Label>
          <Input type="month" value={startMonth} onChange={(e) => setStartMonth((e.target as HTMLInputElement).value)} required />
        </div>

        <div className="space-y-1.5">
          <Label>Término (mês)</Label>
          <Input type="month" value={endMonth} onChange={(e) => setEndMonth((e.target as HTMLInputElement).value)} disabled={noEnd} />
          <label className="mt-2 flex items-center gap-2 text-sm text-foreground/80">
            <Checkbox checked={noEnd} onChange={(e) => setNoEnd((e.target as HTMLInputElement).checked)} />
            Sem data final
          </label>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Observações</Label>
          <Textarea value={notes} onChange={(e) => setNotes((e.target as HTMLTextAreaElement).value)} placeholder="Opcional" />
        </div>

        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </form>
    </Modal>
  )
}
